/**
 * iter312 — Cross-host auth handoff.
 *
 * Because tenants live on different origins (`delhicapitalsacademynj.com`,
 * `demo.vyomos.io`, `my.vyomos.io`, …), `localStorage` cannot be
 * shared between them. Naively `window.location.replace`-ing the user
 * from the platform host to the tenant host drops the JWT and forces a
 * re-login.
 *
 * Fix: when we cross origins, encode the tokens into the URL **fragment**
 * (never search params — fragments never hit the server / logs / proxies
 * / Stripe redirects) and have the destination strip + persist them on
 * boot. Fragment-only payload keeps tokens out of server-access logs.
 */

const HANDOFF_PARAM = 'auth';

const PREVIEW_HOST_SUFFIXES = [
  '.preview.emergentagent.com',
  '.emergent.host',
  'localhost',
  '127.0.0.1',
];

/** True when the current hostname is a developer/preview host that
 *  should NEVER be cross-host-redirected (developer convenience —
 *  staying on the preview keeps `localStorage` intact for iteration). */
export const isPreviewHost = (hostname = window.location.hostname) => {
  const h = (hostname || '').toLowerCase();
  return PREVIEW_HOST_SUFFIXES.some((suf) =>
    suf.startsWith('.') ? h.endsWith(suf) : h === suf,
  );
};

/** Normalise host for comparison: strip `www.` + port. */
export const normHost = (h) =>
  (h || '').replace(/^www\./, '').split(':')[0].toLowerCase();

/** Build a cross-host URL with embedded auth handoff in the fragment.
 *  Strips any leading `www.` from the destination so we never land on
 *  the un-routed www variant. */
export const buildHandoffUrl = (targetHost, path = '/', search = '') => {
  const cleanHost = (targetHost || '').replace(/^www\./i, '');
  const protocol = window.location.protocol === 'http:' ? 'http:' : 'https:';
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const refresh = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
  const storage = localStorage.getItem('authStorage') || 'local';
  let frag = '';
  if (token) {
    const payload = btoa(
      JSON.stringify({ t: token, r: refresh || null, s: storage }),
    );
    frag = `#${HANDOFF_PARAM}=${payload}`;
  }
  return `${protocol}//${cleanHost}${path}${search || ''}${frag}`;
};

/** Run once on app boot. If the URL fragment carries an auth payload,
 *  drop it into the right storage bucket and strip the fragment so
 *  it never leaks into bookmarks/history. Returns true if a payload
 *  was consumed so the caller can decide to soft-reload. */
export const consumeAuthHandoffFromUrl = () => {
  try {
    const hash = window.location.hash || '';
    if (!hash.startsWith('#')) return false;
    const m = hash.match(new RegExp(`(^|&)${HANDOFF_PARAM}=([^&]+)`));
    if (!m) return false;
    const payload = JSON.parse(atob(m[2]));
    if (!payload?.t) return false;
    const storage = payload.s === 'session' ? sessionStorage : localStorage;
    storage.setItem('token', payload.t);
    if (payload.r) storage.setItem('refreshToken', payload.r);
    localStorage.setItem('authStorage', payload.s === 'session' ? 'session' : 'local');
    // Strip the fragment without reloading.
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState({}, '', cleanUrl);
    return true;
  } catch {
    return false;
  }
};
